import * as React from 'react';
import type { ReactNode } from 'react';

import {
  faCheck,
  faCircle,
  faRocket,
  faSeedling,
  faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';

import { AccountTierType } from '@evenfinancial/finance-client';
import type { SaasPlan } from '@evenfinancial/finance-client';

import { EvenButton } from '@portal/ui-lib/src/lib/_atomics/Button/EvenButton';
import { FontAwesomeKitIcon } from '@portal/ui-lib/src/lib/_atomics/FontAwesomeIcon/FontAwesomeKitIcon';
import { KitIconNames } from '@portal/ui-lib/src/lib/_atomics/FontAwesomeIcon/types';
import { EvenTooltip } from '@portal/ui-lib/src/lib/_atomics/ToolTips/EvenTooltip';
import { colors } from '@portal/ui-lib/src/styles/variables';

import './SubscriptionPlanCard.scss';

import type { SubscriptionPlanType } from './types';
import { FeatureKey } from './types';

type RenderFeatureBoolean = (showCheck: boolean) => ReactNode;
const renderFeatureBoolean: RenderFeatureBoolean = (showCheck) => {
  return (
    <FontAwesomeIcon
      icon={showCheck ? faCheck : faXmark}
      style={{
        height: 'auto',
        width: 8,
      }}
    />
  );
};

export interface SubscriptionPlanCardProps {
  currentPlan?: SaasPlan;
  plan: SubscriptionPlanType;
}

const planIconStyle = {
  height: 'auto',
  width: 24,
};

const icons = {
  [AccountTierType.Starter]: (
    <FontAwesomeIcon icon={faRocket} style={planIconStyle} />
  ),
  [AccountTierType.Growth]: (
    <FontAwesomeIcon icon={faSeedling} style={planIconStyle} />
  ),
  [AccountTierType.Scale]: (
    <FontAwesomeKitIcon icon={KitIconNames.Signal} style={planIconStyle} />
  ),
};

export const SubscriptionPlanCard: React.FC<SubscriptionPlanCardProps> = ({
  plan,
}) => {
  return (
    <article>
      <div
        className={classNames('subscription-plan', plan.id, plan.cardClassName)}
      >
        <header
          className={classNames('subscription-plan-header', plan.cardClassName)}
        >
          <span
            className={classNames(
              'fa-layers',
              'fa-fw',
              'fa-4x',
              'subscription-plan-header-icon'
            )}
          >
            <FontAwesomeIcon
              color={colors.white}
              icon={faCircle}
              style={{ height: 'auto', width: 58 }}
            />

            {/* Plan Icon */}
            {icons[plan.id]}
          </span>
          <h1 className="subscription-plan-name" role="plan-name">
            {plan.name}
          </h1>
        </header>

        <svg
          className="subscription-plan-triangle"
          viewBox="0 0 280 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M140 20L0 0h280z" />
        </svg>
        <section className="subscription-plan-subheader">
          <article className="subscription-plan-info">
            <p className="subscription-plan-value">{plan.planFee}</p>
            <h6 className="subscription-plan-label">Monthly Platform Fee</h6>
          </article>
          <article className="subscription-plan-info">
            <p
              className={classNames('subscription-plan-subvalue', 'two-lines')}
              role="seat-numbers"
            >
              {plan.pricing}
            </p>
            <h6 className="subscription-plan-label">
              Dynamic Real Time Pricing (Personal Loans Only)
              <sup>
                <strong>1</strong>
              </sup>
            </h6>
          </article>
          <article className="subscription-plan-info">
            <p className="subscription-plan-subvalue-small">
              <strong>{plan.productOffersLevel}</strong> Product Offers
              Available
            </p>
            <div className="subscription-plan-icons" role="tooltip">
              {plan.integrations.map(({ icon, title }, index) => (
                <EvenTooltip key={index} position="end" text={title}>
                  <FontAwesomeIcon icon={icon} style={planIconStyle} />
                </EvenTooltip>
              ))}
            </div>
            <h6 className="subscription-plan-label">
              Available Integrations
              <sup>
                <strong>2</strong>
              </sup>
            </h6>
          </article>
          <div className="subscription-plan-info">
            <p className="subscription-plan-subvalue" role="seat-number">
              {plan.seatsNumber}
            </p>
            <h6 className="subscription-plan-label">
              Even Tools Seats Included
              <sup>
                <strong>3</strong>
              </sup>
            </h6>
          </div>
        </section>

        <ul className="subscription-plan-features">
          <li>
            <p className="value">
              {renderFeatureBoolean(
                plan.features[FeatureKey.dedicatedPartnerManager]
              )}
            </p>
            <h6 className="label">
              <span>Dedicated Partner Manager</span>
            </h6>
          </li>
        </ul>

        <EvenButton
          large
          className="subscription-plan-activate-button"
          disabled={plan?.isDisabled}
          outlined={plan.actionButtonType === 'outlined'}
          role="button"
          tertiary={plan.actionButtonTertiary}
          onClick={plan.onActionButtonClick}
        >
          {plan.actionButtonTitle}
        </EvenButton>
      </div>
    </article>
  );
};
